import { Filter, FilterMode } from "@/shared/lib/filter/filter";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import { ListFilter } from "lucide-react";
import { useEffect, useState } from "react";
import { Calendar } from "@/shared/components/ui/calendar";
import { DateRange } from "react-day-picker";

export function DateBetweenFilter({
  label,
  filter,
  onFilterChange,
  hidden,
}: {
  label: string;
  filter: Filter<string> | undefined;
  onFilterChange: (filter: Filter<string>) => void;
  hidden?: boolean;
}) {
  // This allows us to accept an undefined filter,
  // which is common in list requests. This makes
  // it nicer to call the component.
  const filterVal = filter ?? {
    active: false,
    mode: FilterMode.Between,
    values: [new Date().toISOString(), new Date().toISOString()],
  };

  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<DateRange | undefined>(() => {
    if (filter && filterVal.values) {
      // we do expect, that there are always two values
      // otherwise, just fail fast
      return {
        from: new Date(filterVal.values[0]),
        to: new Date(filterVal.values[1]),
      };
    }

    return undefined;
  });

  useEffect(() => {
    // deactivate filter if no date is selected
    if (!date && filterVal.active) {
      onFilterChange({
        ...filter,
        active: false,
        values: [],
      });
    }

    // update filter if date is selected
    // we only update the filter if the full range is selected
    if (date?.from && date?.to && filterVal.values) {
      if (
        date.from.toISOString() !== filterVal.values[0] ||
        date.to.toISOString() !== filterVal.values[1]
      ) {
        onFilterChange({
          active: true,
          mode: FilterMode.Between,
          values: [date.from.toISOString(), date.to.toISOString()],
        });
      }
    }
  });

  if (hidden) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between"
          size="sm"
        >
          <ListFilter className="mr-2 h-4 w-4" />
          <span>{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          defaultMonth={date?.from}
          selected={date}
          onSelect={setDate}
          numberOfMonths={2}
        />
      </PopoverContent>
    </Popover>
  );
}
